import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarIcon from "./CalendarIcon";
import "./MonthYearDatePicker.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const HeaderSelect = ({
  label,
  value,
  options,
  onChange,
  className = "",
  openKey,
  activeKey,
  setActiveKey,
}) => {
  const isOpen = activeKey === openKey;
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setActiveKey(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen, setActiveKey]);

  return (
    <div className={`mydp-select ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`mydp-select-trigger${isOpen ? " open" : ""}`}
        onClick={() => setActiveKey(isOpen ? null : openKey)}
        aria-label={label}
        aria-expanded={isOpen}
      >
        <span>{options.find((o) => o.value === value)?.label ?? value}</span>
        <svg className="mydp-select-chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <ul className="mydp-select-menu" role="listbox">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`mydp-select-option${opt.value === value ? " selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  setActiveKey(null);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Date picker with month + year dropdowns (standard app pattern).
 * Avoids rsuite's year×month grid which is hard to navigate for DOB / far dates.
 */
const MonthYearDatePicker = ({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  minYear,
  maxYear,
  minDate,
  maxDate,
  isoWeek = false,
  placement = "bottom-end",
  className = "",
  disabled = false,
}) => {
  const now = new Date();
  const startYear = minYear ?? now.getFullYear() - 100;
  const endYear = maxYear ?? now.getFullYear();
  const years = [];
  for (let y = endYear; y >= startYear; y -= 1) {
    years.push(y);
  }

  const monthOptions = MONTHS.map((label, index) => ({
    value: index,
    label,
  }));
  const yearOptions = years.map((year) => ({
    value: year,
    label: String(year),
  }));

  const popperPlacement =
    placement === "bottomEnd"
      ? "bottom-end"
      : placement === "bottomStart"
        ? "bottom-start"
        : placement;

  return (
    <div className={`month-year-datepicker ${className}`.trim()}>
      <DatePicker
        selected={value || null}
        onChange={onChange}
        dateFormat="dd/MM/yyyy"
        placeholderText={placeholder}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        calendarStartDay={isoWeek ? 1 : 0}
        popperPlacement={popperPlacement}
        showPopperArrow={false}
        calendarClassName="month-year-datepicker-calendar"
        popperClassName="month-year-datepicker-popper"
        className="month-year-datepicker-input"
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <MonthYearHeader
            date={date}
            changeYear={changeYear}
            changeMonth={changeMonth}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
            monthOptions={monthOptions}
            yearOptions={yearOptions}
          />
        )}
      />
      <span className="month-year-datepicker-caret" aria-hidden="true">
        <CalendarIcon />
      </span>
    </div>
  );
};

const MonthYearHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  monthOptions,
  yearOptions,
}) => {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="month-year-datepicker-header">
      <button
        type="button"
        className="month-year-datepicker-nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        ‹
      </button>
      <div className="month-year-datepicker-selects">
        <HeaderSelect
          label="Month"
          className="month"
          openKey="month"
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          value={date.getMonth()}
          options={monthOptions}
          onChange={changeMonth}
        />
        <HeaderSelect
          label="Year"
          className="year"
          openKey="year"
          activeKey={activeKey}
          setActiveKey={setActiveKey}
          value={date.getFullYear()}
          options={yearOptions}
          onChange={changeYear}
        />
      </div>
      <button
        type="button"
        className="month-year-datepicker-nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  );
};

export default MonthYearDatePicker;
